import React, { useEffect, useState, useCallback } from 'react';
import { RefreshCw, Heart, Smartphone, ShoppingBag, MapPin, Building2, Package, CalendarDays, Megaphone, Clock, Sparkles, ChevronDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const gbp = (v) => v == null ? '£0' : new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(v);
const num = (v) => v == null ? '0' : new Intl.NumberFormat('en-GB').format(v);
const fmtDate = (d) => { try { return new Date(String(d).replace(' ', 'T')).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }); } catch { return d || ''; } };
const titleCase = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const cardBorder = 'border border-purple-500/15';
const glassStyle = { background: 'rgba(20,12,40,0.6)', backdropFilter: 'blur(12px)' };
const innerStyle = { background: 'rgba(16,10,30,0.4)' };

const Kpi = ({ value, label, sub, color = 'text-white', testid }) => (
  <div className="rounded-xl p-4 border border-purple-500/10" style={innerStyle} data-testid={testid}>
    <p className={`text-3xl font-bold ${color}`}>{value}</p>
    <p className="text-[11px] text-purple-200/50 mt-1">{label}</p>
    {sub && <p className="text-[10px] text-purple-300/30 mt-0.5">{sub}</p>}
  </div>
);

const BarList = ({ title, icon: Icon, items, max, fmt = num, testid }) => {
  const peak = max || Math.max(1, ...(items || []).map(i => i.count || 0));
  return (
    <div className="rounded-xl p-4 border border-purple-500/10" style={innerStyle} data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="w-3.5 h-3.5 text-fuchsia-300/70" />}
        <p className="text-[10px] text-purple-300/50 uppercase tracking-wider">{title}</p>
      </div>
      <div className="space-y-2">
        {(items || []).filter(i => (i.count || 0) > 0).slice(0, 10).map((it, i) => (
          <div key={i}>
            <div className="flex justify-between text-[11px] mb-1">
              <span className="text-purple-100/70 truncate pr-2">{it.label}</span>
              <span className="text-purple-200/50 shrink-0">{fmt(it.count)}</span>
            </div>
            <div className="h-1.5 rounded-full bg-purple-500/10 overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-fuchsia-500/60 to-purple-400/50" style={{ width: `${Math.round((it.count / peak) * 100)}%` }} />
            </div>
          </div>
        ))}
        {(!items || items.filter(i => (i.count || 0) > 0).length === 0) && (
          <p className="text-[11px] text-purple-300/30">No data yet</p>
        )}
      </div>
    </div>
  );
};

export default function CouplesAppStats() {
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | error
  const [refreshing, setRefreshing] = useState(false);
  const [showNarrative, setShowNarrative] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/operator/couples-stats`);
      const j = await r.json();
      if (j.ok && j.data) { setStats(j.data); setStatus('ok'); }
      else setStatus('error');
    } catch (e) { setStatus('error'); }
    setRefreshing(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const c = stats?.couples || {};
  const a = stats?.app_adoption || {};
  const p = stats?.persona || {};
  const orders = stats?.recent_orders || [];
  const adoptionPct = (a.on_the_app && c.total) ? Math.round((a.on_the_app / c.total) * 100) : null;

  const routes = [...(p.how_did_you_hear || []), ...(p.utm_sources || []).map(u => ({ label: `UTM: ${u.label}`, count: u.count }))];

  return (
    <div className={`rounded-2xl p-6 ${cardBorder}`} style={glassStyle} data-testid="couples-app-section">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-xl bg-rose-500/10 text-rose-300"><Heart className="w-4 h-4" /></div>
          <h3 className="text-sm font-semibold text-purple-200/60 uppercase tracking-wider">WVE Couples App</h3>
        </div>
        <div className="flex items-center gap-3">
          {stats?.generated_at && (
            <span className="text-[10px] text-purple-300/30">Updated {fmtDate(stats.generated_at)}</span>
          )}
          <button onClick={() => { setRefreshing(true); load(); }} disabled={refreshing} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition border border-purple-500/20 ${refreshing ? 'opacity-50' : 'hover:border-purple-400/40'}`} style={glassStyle} data-testid="couples-refresh-btn">
            <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} /> {refreshing ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>
      <p className="text-[11px] text-purple-300/30 mb-5">Live from the couples' booking app — who's booked, who's using it, who they are, and what they're buying.</p>

      {status === 'loading' && <div className="text-center py-8 text-purple-300/40 text-sm">Loading couples app data…</div>}
      {status === 'error' && (
        <div className="rounded-lg px-3 py-2 text-[11px] border border-amber-500/25 bg-amber-500/10 text-amber-300" data-testid="couples-error-note">
          Couldn't reach the couples app stats endpoint. It may still be publishing — try Refresh in a moment.
        </div>
      )}

      {status === 'ok' && (
        <div className="space-y-6">
          {/* Couples + adoption KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="couples-kpis">
            <Kpi value={num(c.total)} label="Total Couples" sub={p.years_span} color="text-white" testid="kpi-total-couples" />
            <Kpi value={num(c.future_weddings)} label="Future Weddings" color="text-emerald-400" testid="kpi-future-weddings" />
            <Kpi value={num(c.past_weddings)} label="Past Weddings" color="text-purple-300/70" testid="kpi-past-weddings" />
            <Kpi value={num(a.on_the_app)} label="On the App" sub={adoptionPct != null ? `${adoptionPct}% adoption` : null} color="text-cyan-300" testid="kpi-on-app" />
            <Kpi value={num(a.not_on_the_app)} label="Not on the App" color="text-amber-400" testid="kpi-not-on-app" />
            <Kpi value={num(a.active_last_7_days)} label="Active · 7 days" color="text-fuchsia-300" testid="kpi-active-7d" />
          </div>

          {/* Persona money summary */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Smartphone className="w-3.5 h-3.5 text-fuchsia-300/70" />
              <p className="text-[10px] text-purple-300/50 uppercase tracking-wider">Persona · Money & Planning</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
              <Kpi value={gbp(p.avg_fee)} label="Avg Booking Fee" testid="kpi-avg-fee" />
              <Kpi value={gbp(p.total_collected)} label="Total Collected" color="text-emerald-400" testid="kpi-total-collected" />
              <Kpi value={num(p.paid_in_full)} label="Paid in Full" color="text-emerald-300" testid="kpi-paid-full" />
              <Kpi value={num(p.on_payment_journey)} label="On Payment Plan" color="text-amber-300" testid="kpi-payment-plan" />
              <Kpi value={`${p.median_planning_months ?? '—'} mo`} label="Median Planning" color="text-cyan-300" testid="kpi-planning" />
              <Kpi value={gbp(p.stripe?.average)} label="Avg Stripe Payment" sub={`${num(p.stripe?.count)} payments`} testid="kpi-stripe-avg" />
            </div>
          </div>

          {/* Persona breakdowns */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="persona-breakdowns">
            <BarList title="Routes to Market" icon={Megaphone} items={routes} testid="persona-routes" />
            <BarList title="Top Towns" icon={MapPin} items={p.top_towns} testid="persona-towns" />
            <BarList title="Top Venues" icon={Building2} items={p.top_venues} testid="persona-venues" />
            <BarList title="Packages" icon={Package} items={p.packages} testid="persona-packages" />
            <BarList title="Spend Brackets" icon={ShoppingBag} items={p.spend_brackets} testid="persona-spend" />
            <BarList title="Planning Time" icon={Clock} items={p.planning_distribution} testid="persona-planning" />
            <BarList title="Wedding Months" icon={CalendarDays} items={p.wedding_months} testid="persona-months" />
            <BarList title="Weekday Split" icon={CalendarDays} items={p.weekday_split} testid="persona-weekday" />
          </div>

          {/* AI narrative */}
          {p.persona_narrative || stats.persona_narrative ? (
            <div className="rounded-xl border border-fuchsia-500/20 bg-gradient-to-br from-fuchsia-500/10 to-purple-900/5" data-testid="persona-narrative">
              <button onClick={() => setShowNarrative(!showNarrative)} className="w-full flex items-center justify-between px-4 py-3 text-left" data-testid="persona-narrative-toggle">
                <span className="flex items-center gap-2 text-xs font-medium text-fuchsia-200/80"><Sparkles className="w-3.5 h-3.5" /> The WVE Couple — AI persona</span>
                <ChevronDown className={`w-4 h-4 text-purple-300/50 transition-transform ${showNarrative ? 'rotate-180' : ''}`} />
              </button>
              {showNarrative && (
                <div className="px-4 pb-4 text-[12px] leading-relaxed text-purple-100/70 whitespace-pre-wrap">
                  {stats.persona_narrative || p.persona_narrative}
                </div>
              )}
            </div>
          ) : null}

          {/* Recent orders */}
          <div className="rounded-xl p-4 border border-purple-500/10" style={innerStyle} data-testid="recent-orders">
            <div className="flex items-center gap-2 mb-3">
              <ShoppingBag className="w-3.5 h-3.5 text-fuchsia-300/70" />
              <p className="text-[10px] text-purple-300/50 uppercase tracking-wider">Recent Add-on Orders</p>
            </div>
            {orders.length === 0 ? (
              <p className="text-[11px] text-purple-300/30">No recent orders.</p>
            ) : (
              <div className="divide-y divide-purple-500/10">
                {orders.slice(0, 3).map((o, i) => (
                  <div key={i} className="flex items-center justify-between py-2 gap-3" data-testid={`order-row-${i}`}>
                    <div className="min-w-0">
                      <p className="text-[12px] text-purple-100/80 truncate">{o.name || '—'}</p>
                      <p className="text-[10px] text-purple-300/40 truncate">{titleCase(o.upsell_type)}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[13px] font-semibold text-emerald-400">{gbp(o.amount)}</p>
                      <p className="text-[10px] text-purple-300/40">{fmtDate(o.ordered_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
